import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorModal from './ErrorModal';
import { ApiError } from '../services/http';
import { subscriptionService } from '../services/subscriptionService';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';

interface CurrentPlanCardProps {
  currentPlan?: {
    name: string;
    price: number;
    status: 'active' | 'trial' | 'trialing' | 'expired';
    nextBilling: string;
    features: Array<string | { description: string; checked?: boolean }>;
  };
}

export default function CurrentPlanCard({ currentPlan }: CurrentPlanCardProps) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const { subscription, loading, error, refreshSubscription } = useSubscriptionStatus();
  const [errorModal, setErrorModal] = useState<{ open: boolean; title?: string; message: string; details?: string }>({ open: false, message: '' });
  const [showCancelModal, setShowCancelModal] = useState(false);

  const cancelSubscription = async () => {
    if (!subscription) return;
    try {
      setIsCanceling(true);
      await subscriptionService.cancelSubscription(subscription.id);
      await refreshSubscription();
      setShowCancelModal(false);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      const message = error instanceof ApiError && error.message ? error.message : 'Error al cancelar la suscripción';
      setErrorModal({ open: true, title: 'Error al cancelar', message });
    } finally {
      setIsCanceling(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      const { url } = await subscriptionService.createBillingPortalSession();
      window.open(url, '_blank');
    } catch (error) {
      const message = error instanceof ApiError && error.message ? error.message : 'No se pudo abrir el portal de facturación';
      setErrorModal({ open: true, title: 'Error', message });
    }
  };

  // Determinar el plan basado en los datos reales de suscripción
  const cleanText = (text: string) => {
    return text
      .replace(/^undefined\s*/i, '')
      .replace(/\s+undefined$/i, '')
      .replace(/undefined/gi, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  };

  const normalizePlanFeatures = (
    features?: Array<string | { description: string; checked?: boolean }>
  ): Array<{ description: string; checked?: boolean }> => {
    if (!features) return [];
    return features
      .map((f) => (typeof f === 'string' ? { description: cleanText(f), checked: true } : { description: cleanText(f.description || ''), checked: f.checked }))
      .filter((f) => Boolean(f.description));
  };

  const getPlanFromSubscription = () => {
    console.log('📋 subscription completa:', subscription);
    
    if (!subscription) {
      console.log('❌ No hay suscripción, usando plan gratis');
      return {
        name: 'Sin Plan Activo',
        price: 0,
        status: 'expired' as const,
        nextBilling: new Date().toISOString(),
        features: [
          '1 Restaurante',
          'Hasta 5 Productos',
          'Soporte por email',
          'Reportes básicos'
        ]
      };
    }

    // Usar la nueva estructura de datos
    const planName = subscription.metadata?.planName || 'Gratis';
    const price = subscription.metadata?.invoice?.amountPaid ? subscription.metadata.invoice.amountPaid / 100 : 0;
    const features = normalizePlanFeatures(
      subscription.features || [
        '1 Restaurante',
        'Hasta 5 Productos',
        'Soporte por email',
        'Reportes básicos'
      ]
    );

    const result = {
      name: planName,
      price: price,
      status: subscription.status,
      nextBilling: subscription.currentPeriodEnd || new Date().toISOString(),
      features: features
    };
    
    console.log('✅ Plan resultante:', result);
    return result;
  };

  const plan = currentPlan || getPlanFromSubscription();

  const getStatusColor = (status: string, canceledAt?: string) => {
    if (canceledAt) {
      return 'bg-orange-100 text-orange-800';
    }
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
      case 'trial':
        return 'bg-blue-100 text-blue-800';
      case 'expired':
      case 'canceled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string, canceledAt?: string) => {
    if (canceledAt) {
      return 'Cancelada (Activa)';
    }
    switch (status) {
      case 'active':
        return 'Activa';
      case 'trialing':
      case 'trial':
        return 'Prueba';
      case 'expired':
        return 'Expirada';
      case 'canceled':
        return 'Cancelada';
      default:
        return 'Desconocido';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getCardBrandIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      default:
        return '💳';
    }
  };

  const isTrial = subscription?.status === 'trialing';
  const isFreePlan = (subscription?.metadata?.planName || '').toLowerCase() === 'gratis';

  if (loading) {
    return (
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-6"></div>
        <div className="h-8 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6">
        <div className="text-center">
          <div className="text-red-500 text-2xl mb-2">⚠️</div>
          <p className="text-red-600">{error}</p>
          <button
            onClick={refreshSubscription}
            className="mt-4 px-4 py-2 bg-[#22B6B2] text-white rounded-xl hover:bg-[#1a8f8c] transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-200 p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-[#2C4358] mb-2">
            {plan.name === 'Sin Plan Activo' ? 'Plan Actual' : `Plan ${plan.name.charAt(0).toUpperCase() + plan.name.slice(1)}`}
          </h3>
          <p className="text-[#85929e] text-sm">
            {plan.name === 'Sin Plan Activo' ? 'Sin suscripción activa' : 'Tu plan de suscripción actual'}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(plan.status, subscription?.canceledAt)}`}>
            {getStatusText(plan.status, subscription?.canceledAt)}
          </span>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 text-[#85929e] hover:text-[#2C4358] transition-colors"
          >
            {isExpanded ? '−' : '+'}
          </button>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-sm text-[#85929e] mb-1">Precio</div>
          <div className="text-2xl font-bold text-[#2C4358]">
            {plan.price > 0 ? formatCurrency(plan.price) : (isFreePlan ? 'Gratuito' : '—')}
          </div>
          <p className="text-[#85929e] text-sm">
            {isTrial ? 'En prueba' : (isFreePlan ? 'Plan gratis' : '/mes')}
          </p>
        </div>
        <div className="bg-gray-50 rounded-2xl p-4">
          <div className="text-sm text-[#85929e] mb-1">Próxima facturación</div>
          <div className="text-lg font-semibold text-[#2C4358]">
            {formatDate(plan.nextBilling)}
          </div>
          <p className="text-[#85929e] text-sm">
            {plan.status === 'active' ? 'Renovación automática' : 'Sin renovación'}
          </p>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && subscription && (
        <div className="space-y-6 animate-fadeIn">
          {/* Subscription Details */}
          <div className="bg-blue-50 rounded-2xl p-4">
            <h5 className="font-semibold text-[#2C4358] mb-3">Detalles de Suscripción</h5>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-[#85929e]">Creada:</span>
                <div className="text-[#2C4358]">{formatDate(subscription.createdAt)}</div>
              </div>
              <div>
                <span className="text-[#85929e]">Actualizada:</span>
                <div className="text-[#2C4358]">{formatDate(subscription.updatedAt)}</div>
              </div>
            </div>
          </div>

          {/* Invoice Information */}
          {subscription.metadata?.invoice && (
            <div className="bg-green-50 rounded-2xl p-4">
              <h5 className="font-semibold text-[#2C4358] mb-3">Información de Facturación</h5>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#85929e]">Estado del pago:</span>
                  <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    subscription.metadata.invoice.status === 'paid' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {subscription.metadata.invoice.status === 'paid' ? 'Pagado' : 'Pendiente'}
                  </div>
                </div>
                <div>
                  <span className="text-[#85929e]">Monto pagado:</span>
                  <div className="text-[#2C4358] font-semibold">
                    {formatCurrency(subscription.metadata.invoice.amountPaid / 100)}
                  </div>
                </div>
                <div>
                  <span className="text-[#85929e]">Fecha de pago:</span>
                  <div className="text-[#2C4358]">
                    {new Date(subscription.metadata.invoice.created * 1000).toLocaleDateString('es-ES')}
                  </div>
                </div>
              </div>
              {subscription.metadata.invoice.pdfUrl && (
                <div className="mt-3">
                  <a 
                    href={subscription.metadata.invoice.pdfUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#22B6B2] hover:text-[#1a8f8c] text-sm font-medium"
                  >
                    📄 Ver factura
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Cancellation Information */}
          {subscription.canceledAt && (
            <div className="bg-red-50 rounded-2xl p-4">
              <h5 className="font-semibold text-[#2C4358] mb-3">Información de Cancelación</h5>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-red-600 text-sm">⚠️</span>
                </div>
                <div className="flex-1">
                  <div className="text-sm text-[#2C4358] font-medium">
                    Suscripción cancelada
                  </div>
                  <div className="text-xs text-[#85929e]">
                    Cancelada el {formatDate(subscription.canceledAt)}
                  </div>
                  <div className="text-xs text-[#85929e] mt-1">
                    Acceso hasta: {formatDate(subscription.currentPeriodEnd)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-red-600 font-medium">
                    Sin renovación
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods */}
          {subscription.paymentMethods && subscription.paymentMethods.length > 0 && (
            <div className="bg-purple-50 rounded-2xl p-4">
              <h5 className="font-semibold text-[#2C4358] mb-3">Métodos de Pago ({subscription.paymentMethods.length})</h5>
              
              {/* All Payment Methods */}
              <div className="space-y-3">
                {subscription.paymentMethods.map((method) => (
                  <div key={method.id} className={`flex items-center justify-between rounded-xl p-3 ${
                    subscription.defaultPaymentMethod?.id === method.id 
                      ? 'bg-gradient-to-r from-purple-100 to-purple-200 border-2 border-purple-300' 
                      : 'bg-white'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getCardBrandIcon(method.card.brand)}</span>
                      <div>
                        <div className="font-semibold text-[#2C4358]">
                          {method.card.brand.toUpperCase()} •••• {method.card.last4}
                        </div>
                        <div className="text-sm text-[#85929e]">
                          Expira {method.card.exp_month}/{method.card.exp_year}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {subscription.defaultPaymentMethod?.id === method.id && (
                        <span className="px-2 py-1 bg-purple-600 text-white text-xs rounded-full font-medium">
                          Por defecto
                        </span>
                      )}
                      <div className="text-xs text-[#85929e]">
                        {new Date(method.created * 1000).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Features */}
          <div className="bg-orange-50 rounded-2xl p-4">
            <h5 className="font-semibold text-[#2C4358] mb-3">Características Incluidas</h5>
            <div className="grid grid-cols-2 gap-3">
              {normalizePlanFeatures(plan.features).map((feature) => (
                <div key={feature.description} className="flex items-center space-x-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${feature.checked ? 'bg-green-100' : 'bg-gray-200'}`}>
                    <span className={`text-xs ${feature.checked ? 'text-green-600' : 'text-gray-500'}`}>✓</span>
                  </div>
                  <span className={`text-sm ${feature.checked ? 'text-[#2C4358]' : 'text-gray-500 line-through'}`}>
                    {feature.description}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={() => navigate('/pricing')}
          className="w-full sm:flex-1 bg-gradient-to-r from-[#22B6B2] to-[#2C4358] text-white py-3 px-4 rounded-2xl font-semibold hover:shadow-lg transition-all duration-300 sm:transform sm:hover:scale-105"
        >
          {plan.status === 'trialing' || plan.status === 'trial' ? 'Actualizar Plan' : 'Cambiar Plan'}
        </button>
        
        {subscription && subscription.status === 'active' && (
          <button
            onClick={() => setShowCancelModal(true)}
            disabled={isCanceling}
            className="w-full sm:w-auto px-4 py-3 border-2 border-red-500 text-red-500 rounded-2xl font-semibold hover:bg-red-500 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCanceling ? 'Cancelando...' : 'Cancelar'}
          </button>
        )}

        <button
          onClick={openBillingPortal}
          className="w-full sm:w-auto px-4 py-3 border-2 border-[#22B6B2] text-[#22B6B2] rounded-2xl font-semibold hover:bg-[#22B6B2] hover:text-white transition-all duration-300"
        >
          Gestionar Suscripción
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#2C4358]">¿Cancelar suscripción?</h3>
              <button onClick={() => setShowCancelModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-red-100 text-red-600 rounded-full flex items-center justify-center">⚠️</div>
                <div>
                  <p className="text-[#2C4358] font-semibold">Perderás acceso a las funciones premium</p>
                  <p className="text-[#85929e] text-sm">Podrás seguir usando el plan gratis sin órdenes desde mesa ni cocina en tiempo real.</p>
                </div>
              </div>
              <ul className="list-disc list-inside text-[#85929e] text-sm">
                <li>Mantén tu historial y configuración</li>
                <li>Siempre puedes reactivar más adelante</li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="w-full sm:flex-1 px-4 py-3 rounded-2xl bg-gray-100 text-[#2C4358] font-semibold hover:bg-gray-200"
              >
                Me quedo
              </button>
              <button
                onClick={openBillingPortal}
                className="w-full sm:w-auto px-4 py-3 rounded-2xl border-2 border-[#22B6B2] text-[#22B6B2] font-semibold hover:bg-[#22B6B2] hover:text-white"
              >
                Gestionar Pago
              </button>
              <button
                onClick={cancelSubscription}
                disabled={isCanceling}
                className={`w-full sm:w-auto px-4 py-3 rounded-2xl font-semibold text-white ${isCanceling ? 'bg-red-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'}`}
              >
                {isCanceling ? 'Cancelando...' : 'Confirmar Cancelación'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ErrorModal
        isOpen={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        details={errorModal.details}
        onClose={() => setErrorModal({ open: false, message: '' })}
      />
    </div>
  );
} 